import { ButtonHTMLAttributes, Fragment, useMemo, useState } from "react";
import { X } from "lucide-react";
import Button from "../button";
import { SelectGroup, SelectOption } from "../control/types";
import Input from "../input";
import DropdownItem from "./dropdownItem";
import DropdownMenu from "./dropdownMenu";
import { twMerge } from "tailwind-merge";

export interface DropdownOptionsProps<T extends SelectOption> {
  value?: ButtonHTMLAttributes<T>["value"];
  options: SelectGroup<T>[] | T[];
  clearable?: boolean;
  searchable?: boolean;
  className?: string;
  isOptionDisabled?: (options: T) => boolean;
}

export default function DropdownOptions<T extends SelectOption>(
  props: Readonly<DropdownOptionsProps<T>>,
) {
  const { value, options, searchable, className, isOptionDisabled } = props;

  const [filter, setFilter] = useState<string>("");
  const filteredOptions = useMemo(() => {
    if (!filter.trim().length) return options;

    return options.filter((option) => {
      if ("options" in option) {
        return;
      }

      return option.label.toLowerCase().includes(filter.toLowerCase());
    });
  }, [options, filter]);

  const isSelected = (option: T) =>
    Array.isArray(value) ? value.includes(option.id) : value === option.id;

  return (
    <DropdownMenu className={twMerge("flex flex-col gap-0 py-0", className)}>
      {searchable && (
        <div className="flex flex-row border-b border-b-slate-300">
          <Input
            className="flex-1 !rounded-none !ring-0 !border-0"
            placeholder="Search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <Button
            mode="flat"
            variant="light"
            className="rounded-none border-l border-l-slate-300"
            onClick={() => setFilter("")}
          >
            <X size="16" />
          </Button>
        </div>
      )}
      <div className="overflow-auto py-2">
        {filteredOptions.map((option) =>
          "options" in option ? (
            <Fragment key={option.label}>
              <div className="text-xs text-gunmetal-500 px-3 pt-2">
                {option.label}
              </div>
              {option.options.map((option) => (
                <DropdownItem
                  key={option.id}
                  eventKey={option.id}
                  disabled={isOptionDisabled?.(option)}
                  selected={isSelected(option)}
                >
                  {option.label}
                </DropdownItem>
              ))}
            </Fragment>
          ) : (
            <DropdownItem
              key={option.id}
              eventKey={option.id}
              disabled={isOptionDisabled?.(option)}
              selected={isSelected(option)}
            >
              {option.label}
            </DropdownItem>
          ),
        )}
      </div>
    </DropdownMenu>
  );
}
